import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { UserCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const OnboardingPage = () => {
    const { user, token, setUser } = useContext(AuthContext);
    const navigate = useNavigate();
    // Google'dan gelen ismi otomatik doldur (varsa)
    const [name, setName] = useState(user?.googleName || user?.name || '');

    // Eğer zaten profil tamamlanmışsa ana sayfaya yönlendir
    React.useEffect(() => {
        if (!user) {
            navigate('/login');
        } else if (user.profileCompleted) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name || name.trim().length < 2) {
            toast.error('Please enter your full name (at least 2 characters)');
            return;
        }

        const loadingToast = toast.loading('Setting up your profile...');

        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.put(
                `${API_BASE_URL}/api/auth/complete-profile`,
                { name: name.trim() },
                config
            );

            // Kullanıcı bilgilerini güncelle
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));

            toast.success('Welcome to AircoreDynamics! 🎉', { id: loadingToast });
            navigate('/');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.msg || 'Failed to update profile', { id: loadingToast });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-lg w-full card p-8 md:p-12"
            >
                {/* Icon */}
                <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full bg-gradient-to-br from-light-primary/20 to-blue-500/20 dark:from-dark-primary/20 dark:to-cyan-400/20">
                        <RocketLaunchIcon className="h-12 w-12 text-light-primary dark:text-dark-primary" />
                    </div>
                </div>

                {/* Welcome Message */}
                <h1 className="text-3xl md:text-4xl font-bold text-center mb-3">
                    Welcome to{' '}
                    <span className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                        AircoreDynamics
                    </span>
                </h1>
                <p className="text-center text-light-text/70 dark:text-dark-text/70 mb-8">
                    Let's personalize your experience! Please tell us your name.
                </p>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                            <UserCircleIcon className="h-5 w-5 text-light-primary dark:text-dark-primary" />
                            Full Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your full name"
                            className="input-field text-lg"
                            autoFocus
                            required
                            minLength={2}
                        />
                        <p className="text-xs text-light-text/60 dark:text-dark-text/60 mt-2">
                            This name will be displayed on your certificates and profile
                        </p>
                    </div>

                    <button
                        type="submit"
                        className="btn-primary w-full !text-lg !py-3"
                    >
                        Continue
                    </button>
                </form>

                {/* Info */}
                <div className="mt-8 p-4 rounded-lg bg-light-primary/5 dark:bg-dark-primary/5 border border-light-primary/20 dark:border-dark-primary/20">
                    <p className="text-sm text-center text-light-text/70 dark:text-dark-text/70">
                        🎓 Start your aviation learning journey with professional courses, 
                        research articles, and earn certificates!
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default OnboardingPage;
